import React, { useEffect, useState } from "react";
import { Col, Row, Spin } from "antd";
import apiService from "../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";
import AccountSummaryMetricCol from "./AccountSummaryMetricCol";
import "../pages/billing.css";
import SmartIcon from "../../components/generic/genericIcon/SmartIcon";
import { getCurrentTimeZone } from "../../utils/dateutils";

const formatCurrency = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatShortMdY = (d) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);

const formatMonth = (val, withYear = true) =>
  new Date(val + "-01").toLocaleString("default", {
    month: "long",
    ...(withYear && { year: "numeric" }),
  });

const AccountSummary = ({ refreshKey = 0, officeId }) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await apiService.get(
          "niramayafoundation/billing/api/v1/dashboard",
          {
            organizationIdentifier,
            timezone: getCurrentTimeZone(),
            ...(officeId != null && { officeId }),
          },
        );
        if (!cancelled) {
          setBillingData(response ?? null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [organizationIdentifier, officeId, refreshKey]);

  const totalDue = billingData?.totalAmountDue ?? 0;
  const currentCharges = billingData?.currentCharges ?? 0;
  const paymentsReceived = billingData?.payment ?? 0;
  const carriedForward = billingData?.previousBalance ?? 0;

  return (
    <div className="account-summary">
      <Row gutter={[16, 16]} wrap className="account-summary-grid">
        <Col xs={24} md={16}>
          <section
            className="account-summary-hero"
            aria-labelledby="account-summary-total-label"
          >
            {loading ? (
              <div
                className="account-summary-hero-loading"
                role="status"
                aria-live="polite"
                aria-busy="true"
                aria-label="Loading billing summary"
              >
                <Spin size="medium" />
              </div>
            ) : (
              <div className="account-summary-hero-main">
                <div className="account-summary-hero-left">
                  <div className="account-summary-hero-icon" aria-hidden>
                    <SmartIcon iconKey="amountDue" color="#fff" />
                  </div>
                  <div className="account-summary-hero-text">
                    <h2
                      id="account-summary-total-label"
                      className="account-summary-hero-title"
                    >
                      Total Amount Due
                    </h2>
                    <p className="account-summary-hero-value">
                      {formatCurrency(totalDue)}
                    </p>
                    <p className="account-summary-metric-sub">
                      Total outstanding balance to be paid
                    </p>
                  </div>
                </div>
                <div className="account-summary-hero-right">
                  <span className="account-summary-hero-pill">
                    Bill Generated • {formatShortMdY(billingData?.invoiceDate)}
                  </span>
                  <span className="account-summary-hero-pill">
                    Due By • {formatShortMdY(billingData?.payByDate)}
                  </span>
                </div>
              </div>
            )}
          </section>
        </Col>
        {billingData?.HVASubscriptionAmount && (
          <AccountSummaryMetricCol
            loading={loading}
            iconKey="workforce"
            title="HVA Membership"
            value={formatCurrency(billingData?.HVASubscriptionAmount)}
            subtitle={
              <span className="account-summary-metric-meta">
                {billingData?.HVASubscriptionAmountPaidDate && (
                  <span>Last paid on {formatShortMdY(billingData?.HVASubscriptionAmountPaidDate)}</span>
                )}
                {billingData?.HVASubscriptionExpiryDate && (
                  <span>Expires on {formatShortMdY(billingData?.HVASubscriptionExpiryDate)}</span>
                )}
                {billingData?.HVASubscriptionExpiryDate && (
                  <span>Membership Holder: {billingData?.primaryBillingContact}</span>
                )}
                <span>Dues Automatically paid by PDV Billing</span>
              </span>
            }
          />
        )}
        <AccountSummaryMetricCol
          loading={loading}
          iconKey="dollar"
          title="Next Billing Date"
          value={formatShortMdY(billingData?.nextMonthInvoiceDate)}
          subtitle="When your next invoice will be generated"
        />
        <AccountSummaryMetricCol
          loading={loading}
          iconKey="dollar"
          title={<>Latest Invoice ({formatMonth(billingData?.billingPeriod)})</>}
          value={formatCurrency(currentCharges)}
          subtitle={<>Charges for services used in {formatMonth(billingData?.billingPeriod, false)}</>}
        />
        <AccountSummaryMetricCol
          loading={loading}
          iconKey="previousBalance"
          title="Previous Balance"
          value={formatCurrency(carriedForward)}
          subtitle="Unpaid balance from prior months"
        />
        <AccountSummaryMetricCol
          loading={loading}
          iconKey="creditCard"
          title="Payments & Credits"
          value={formatCurrency(paymentsReceived)}
          subtitle="Total payments and adjustments applied"
        />
      </Row>
    </div>
  );
};

export default AccountSummary;
